import { importProvidersFrom, inject } from '@angular/core';
import { ApiModule } from '@api/api.module';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MarkdownModule } from 'ngx-markdown';
import { NgxCaptchaModule } from 'ngx-captcha';
import { NgxDaterangepickerMd } from 'ngx-daterangepicker-material-dayjs';
import { NgxGpAutocompleteModule } from '@angular-magic/ngx-gp-autocomplete';
import { RepositoryModule } from '@repository/repository.module';
import { RouterModule } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { TranslateLoader, TranslateModule, TranslateParser, TranslateService } from '@ngx-translate/core';
import { HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { WINDOW_PROVIDERS } from '@providers/window.provider';
import { EnvironmentService } from '@services/environment.service';
import { CentralServerService } from '@services/central-server.service';
import { AuthorizationService } from '@services/authorization.service';
import { NavigationService } from '@services/navigation/navigation.service';
import { ComponentService } from '@services/component.service';
import { SpinnerService } from '@services/spinner.service';
import { LocaleService } from '@services/locale.service';
import { LocalStorageService } from '@services/local-storage.service';
import { MessageService } from '@services/message.service';
import { WindowService } from '@services/window.service';
import { StripeService } from '@services/stripe.service';
import { SentryService } from '@services/sentry.service';
import { MixpanelService } from '@services/mixpanel.service';
import { envProvider } from '@providers/env.provider';
import { appInitProvider } from '@utils/app-factory';
import { matTabsConfigProvider } from '@providers/angular-material.provider';
import { Loader } from '@googlemaps/js-api-loader';
import { SentryUtils } from '@utils/sentry/sentry-utils';
import { PlatformService } from '@services/platform.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { Observable, catchError, combineLatest, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { OpenIdConfiguration, provideAuth, StsConfigLoader } from 'angular-auth-oidc-client';
import { TenantSSOOptionsService } from '@services/tenant-sso-options.service';
import { SSOProvider } from './app/types/login/SSO';
import { httpInterceptorProviders } from './app/interceptors';
import { CustomTranslateDefaultParser } from './app/i18n/custom-translate.parse';
import { I18TranslateLoader } from './app/i18n/i18-translate.loader';
import { SidebarModule } from './app/sidebar/sidebar.module';
import { MaterialModule } from './app/material/material.module';
import { AppRoutingModule } from './app/app-routing.module';
import { SsoCallbackComponent } from './app/pages/authentication/sso-callback/sso-callback.component';

class MultiStsConfigLoader extends StsConfigLoader {
  public constructor(private readonly configs$: Observable<OpenIdConfiguration[]>) {
    super();
  }
  public override loadConfigs(): Observable<OpenIdConfiguration[]> {
    return this.configs$;
  }
}

export function stsConfigLoaderFactory(): StsConfigLoader {
  // The OIDC client relies on browser APIs and should not be initialized on the server
  if (!inject(PlatformService).isBrowser()) {
    return new MultiStsConfigLoader(of([]));
  }

  const environmentService = inject(EnvironmentService);
  const windowService = inject(WindowService);
  const tenantSSOOptionsService = inject(TenantSSOOptionsService);
  const redirectUrl = `${windowService.getOrigin()}/auth/${SsoCallbackComponent.SSO_CALLBACK_PATH}`;

  const buildOidcConfig = (
    configId: SSOProvider,
    authority: string,
    clientId: string,
    scope: string,
  ): OpenIdConfiguration => ({
    configId,
    authority,
    clientId,
    scope,
    redirectUrl,
    // disables postLoginRoute so we handle the redirect ourselves after backend token exchange
    triggerAuthorizationResultEvent: true,
    responseType: 'code',
  });

  const configs$ = combineLatest([
    environmentService.init(),
    tenantSSOOptionsService.tenantSSOOptions$.pipe(catchError(() => of(null))),
  ]).pipe(
    map(([cfg, ssoOptions]) => {
      const configs: OpenIdConfiguration[] = [];

      if (cfg.auth0Config) {
        configs.push(
          buildOidcConfig(SSOProvider.AUTH0, cfg.auth0Config.domain, cfg.auth0Config.clientId, 'openid profile email'),
        );
      }

      if (ssoOptions?.provider === SSOProvider.COGNITO && ssoOptions.issuer && ssoOptions.dashboardClientId) {
        configs.push(
          buildOidcConfig(SSOProvider.COGNITO, ssoOptions.issuer, ssoOptions.dashboardClientId, 'email openid'),
        );
      }

      return configs;
    }),
  );

  return new MultiStsConfigLoader(configs$);
}

export const appProviders = [
  importProvidersFrom(
    ApiModule,
    AppRoutingModule,
    BrowserModule,
    FormsModule,
    MarkdownModule.forRoot(),
    MaterialModule,
    NgxCaptchaModule,
    NgxDaterangepickerMd.forRoot(),
    NgxGpAutocompleteModule,
    ReactiveFormsModule,
    RepositoryModule,
    RouterModule,
    SidebarModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: I18TranslateLoader,
        deps: [HttpClient],
      },
      parser: {
        provide: TranslateParser,
        useClass: CustomTranslateDefaultParser,
      },
      defaultLanguage: 'en',
    }),
  ),
  WINDOW_PROVIDERS,
  EnvironmentService,
  CentralServerService,
  AuthorizationService,
  NavigationService,
  ComponentService,
  SpinnerService,
  LocaleService,
  LocalStorageService,
  MessageService,
  TranslateService,
  WindowService,
  StripeService,
  SentryService,
  MixpanelService,
  envProvider,
  appInitProvider,
  matTabsConfigProvider,
  {
    provide: Loader,
    useValue: new Loader({
      apiKey: 'AIzaSyAlrUfxOfuXFDifAw3MtyxYWf36KnvWjCs',
      libraries: ['places'],
    }),
  },
  ...SentryUtils.getSentryProviders(),
  provideAuth({
    loader: {
      provide: StsConfigLoader,
      useFactory: stsConfigLoaderFactory,
    },
  }),
  httpInterceptorProviders,
  provideHttpClient(withInterceptorsFromDi(), withFetch()),
  PlatformService,
  provideAnimations(),
];

export const appConfig = {
  providers: appProviders,
};
